import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import api from "../api";
import { Spinner, StatusPill, Pill, relTime } from "../ui";

const filters = [
  { id: "", label: "All" },
  { id: "INVESTIGATING", label: "In review" },
  { id: "ANALYZED", label: "Analyzed" },
  { id: "DECIDED", label: "Decided" },
  { id: "RESOLVED", label: "Resolved" },
];

export default function Cases() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      api.get("/cases", { params }).then((r) => setData(r.data)).catch(() => setData({ cases: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [q, status]);

  return (
    <div data-testid="cases-page">
      <p className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B5A58A]/80 mb-4">Cases</p>
      <h1 className="font-serif-display font-light tracking-tighter text-5xl md:text-6xl text-white mb-10">Case history</h1>

      <div className="relative mb-6">
        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" strokeWidth={1.5} />
        <input
          data-testid="cases-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search title or content"
          className="w-full bg-[#26241F] border border-white/[0.08] rounded-xl pl-12 pr-5 py-4 text-sm text-white placeholder:text-stone-500 outline-none focus:border-[#B5A58A]/50 transition-colors duration-300"
        />
      </div>

      <div className="flex gap-2 flex-wrap mb-8">
        {filters.map((f) => (
          <button
            key={f.id}
            data-testid={`cases-filter-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setStatus(f.id)}
            className={`rounded-full px-4 py-2 font-mono-label text-[9px] tracking-[0.2em] uppercase border transition-colors duration-300 ${
              status === f.id ? "border-[#B5A58A] text-[#B5A58A]" : "border-white/[0.12] text-stone-400 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {!data ? (
        <Spinner label="Searching the files…" />
      ) : (data.cases || []).length === 0 ? (
        <p className="text-sm text-stone-500" data-testid="cases-empty">No cases found.</p>
      ) : (
        <div className="space-y-3">
          {data.cases.map((c) => (
            <Link
              key={c.id}
              to={`/app/case/${c.id}`}
              data-testid={`cases-row-${c.public_case_number}`}
              className="group flex items-center justify-between gap-6 bg-[#26241F] border border-white/[0.08] rounded-2xl px-6 py-5 hover:border-[#B5A58A]/40 transition-colors duration-300"
            >
              <div className="min-w-0">
                <p className="font-mono-label text-[9px] tracking-[0.25em] text-[#B5A58A]/80 mb-1.5">{c.public_case_number}</p>
                <h3 className="font-serif-display font-light tracking-tight text-xl text-white truncate">{c.title}</h3>
                <p className="font-mono-label text-[9px] tracking-[0.15em] uppercase text-stone-500 mt-1.5">
                  {c.advisor?.name || "Auto"} · {relTime(c.last_updated_at)}
                </p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <StatusPill status={c.status} />
                <ArrowUpRight className="w-4 h-4 text-stone-500 group-hover:text-[#B5A58A] transition-colors duration-300" strokeWidth={1.5} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
